/**
 * Accordion Component
 */

import { BuilderActions } from '../../../shared'
import { IBuilderComponent } from '../../base'
import { BaseAiComponentInput } from '../../registry'
import type {
  ColorAdaptiveValue,
  ColorVariant,
  ComponentAlignment,
  GradientDirection,
  ResponsiveValue,
  SpacingInputValue,
  TypographyValue,
  VariableSelectValue,
  VariableTextValue,
} from '../../types'
import {
  deepMerge,
  parseAlignmentFormat,
  parseColorFormat,
  parseResponsiveFormat,
  parseSpacingFormat,
  parseToVariableSelect,
  parseToVariableText,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import {
  formatFromVariableText,
  formatFromVariableSelect,
  formatSpacingFormat,
  formatTypographyFormat,
  formatWidthFormat,
  formatComponentAlignmentFormat,
  formatColorFormat,
} from '../../format-utils'
import accordionTemplate from './accordion.template.json'

const ACCORDION_DEFAULT_TEMPLATE = accordionTemplate as unknown as IAccordionComponentSettings

export interface IAccordionComponentSettings {
  // Data settings
  'accordion-data-source': string
  'accordion-data-title': string
  'accordion-data-content': string
  'accordion-data-icon': VariableSelectValue
  'accordion-data-expanded-icon': string
  'accordion-data-comp-title': VariableTextValue
  'accordion-data-sort-type': string

  // Design settings
  'accordion-design-add-button-color': any
  'accordion-design-width': ResponsiveValue<string[] | number[]>
  'accordion-design-search-width': ResponsiveValue<string[] | number[]>
  'accordion-design-alignment-component': ResponsiveValue<ComponentAlignment>
  'accordion-design-alignment-content-accordion': ResponsiveValue<any>
  'accordion-design-alignment-group-header': ResponsiveValue<any>
  'accordion-design-alignment-title': ResponsiveValue<any>
  'accordion-design-alignment-title-accordion': ResponsiveValue<any>
  'accordion-design-group-search-align': ResponsiveValue<any>
  'accordion-design-component-margin': ResponsiveValue<SpacingInputValue>
  'accordion-design-title-margin': ResponsiveValue<SpacingInputValue>
  'accordion-design-group-header-margin': ResponsiveValue<SpacingInputValue>
  'accordion-design-search-margin': ResponsiveValue<SpacingInputValue>
  'accordion-design-search-color': ResponsiveValue<any>
  'accordion-design-search-corner-radius': ResponsiveValue<string>
  'accordion-design-items-per-page': number[]
  'accordion-design-text-color': any
  'accordion-design-text-color-solid': ColorAdaptiveValue<ColorVariant>
  'accordion-design-text-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'accordion-design-text-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'accordion-design-text-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'accordion-design-content-color': any
  'accordion-design-content-color-solid': ColorAdaptiveValue<ColorVariant>
  'accordion-design-content-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'accordion-design-content-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'accordion-design-content-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'accordion-design-group-header-color': any
  'accordion-design-group-header-color-solid': ColorAdaptiveValue<ColorVariant>
  'accordion-design-group-header-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'accordion-design-group-header-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'accordion-design-group-header-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'accordion-design-title-color': any
  'accordion-design-title-color-solid': ColorAdaptiveValue<ColorVariant>
  'accordion-design-title-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'accordion-design-title-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'accordion-design-title-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'accordion-design-typography': {
    'accordion-design-content-typography': ResponsiveValue<TypographyValue>
    'accordion-design-group-header-typography': ResponsiveValue<TypographyValue>
    'accordion-design-header-typography': ResponsiveValue<TypographyValue>
    'accordion-design-title-typography': ResponsiveValue<TypographyValue>
  }

  // Actions
  'accordion-action-add-button-click': BuilderActions

  // Behavior settings
  'accordion-behavior-add-button-position': string
  'accordion-behavior-add-button-show': boolean
  'accordion-behavior-add-button-visibility': any
  'accordion-behavior-search-results-info': boolean
  'accordion-behavior-user-search': boolean
}

export interface IAccordionComponent extends IBuilderComponent {
  componentId: 'accordion'
  groupId: 'list'
  settings: IAccordionComponentSettings
}

/**
 * Accordion AI Input
 */
export interface AiAccordionInput extends BaseAiComponentInput {
  dataSource: string // Required: Table ID
  accordionTitle?: string[] // Format: ["text:Accordion"] or ["text:FAQ for ", "cr:category"]
  
  // Data columns
  titleColumn?: string // Column ID for accordion title
  contentColumn?: string // Column ID for accordion content
  icon?: string // Icon name (e.g., "expand_more")
  expandedIcon?: string // Expanded icon name (e.g., "expand_less")
  
  // Sort & Filter
  sortBy?: string // Column ID to sort by
  sortType?: 'asc' | 'desc' // Sort direction
  limit?: number // Number of items to display
  
  // Styling
  width?: string // Format: "w:100%" or "w:100% de:w:60%"
  alignment?: string // Format: "center" or "left"
  titleAlignment?: string // Title alignment
  margin?: string // Format: "m:16px"
  
  // Colors
  textColor?: string // Text color
  contentColor?: string // Content color
  titleColor?: string // Title color
  groupHeaderColor?: string // Group header color
  
  // Typography
  typography?: {
    title?: string // Title typography
    header?: string // Header typography
    content?: string // Content typography
    groupHeader?: string // Group header typography
  }
  
  // Behavior
  showSearch?: boolean // Show search bar
  showAddButton?: boolean // Show add button
  multiExpand?: boolean // Allow multiple items expanded at once
}

export class AccordionComp {
  getTemplate(): IAccordionComponentSettings {
    return ACCORDION_DEFAULT_TEMPLATE
  }

  /**
   * format accordion component settings to AI accordion input
   */
  format(settings: IAccordionComponentSettings): Omit<AiAccordionInput, 'type'> {
    const result: Omit<AiAccordionInput, 'type'> = {
      dataSource: settings['accordion-data-source'] || '',
    }

    // Data - Source (required)
    if (settings['accordion-data-source']) {
      result.dataSource = settings['accordion-data-source']
    }

    // Data - Columns
    if (settings['accordion-data-title']) {
      result.titleColumn = settings['accordion-data-title']
    }

    if (settings['accordion-data-content']) {
      result.contentColumn = settings['accordion-data-content']
    }

    // Data - Icon
    if (settings['accordion-data-icon']) {
      const icon = formatFromVariableSelect(settings['accordion-data-icon'])
      if (icon && icon.startsWith('icon:')) {
        result.icon = icon.substring(5)
      } else {
        result.icon = icon
      }
    }

    if (settings['accordion-data-expanded-icon']) {
      result.expandedIcon = settings['accordion-data-expanded-icon']
    }

    // Data - Component Title
    if (settings['accordion-data-comp-title']) {
      result.accordionTitle = formatFromVariableText(settings['accordion-data-comp-title'])
    }

    // Data - Sort
    if (settings['accordion-data-sort-type']) {
      result.sortType = settings['accordion-data-sort-type'] as 'asc' | 'desc'
    }

    // Design - Width
    if (settings['accordion-design-width']) {
      result.width = formatWidthFormat(settings['accordion-design-width'])
    }

    // Design - Alignment
    if (settings['accordion-design-alignment-component']) {
      result.alignment = formatComponentAlignmentFormat(settings['accordion-design-alignment-component'])?.replace(/^h:/, '') || undefined
    }

    // Design - Title Alignment
    if (settings['accordion-design-alignment-title']) {
      const allSame = Object.values(settings['accordion-design-alignment-title']).every((v) => v === Object.values(settings['accordion-design-alignment-title'])[0])
      if (allSame) {
        result.titleAlignment = String(Object.values(settings['accordion-design-alignment-title'])[0])
      } else {
        result.titleAlignment = formatComponentAlignmentFormat(settings['accordion-design-alignment-title'] as any)?.replace(/^h:/, '') || undefined
      }
    }

    // Design - Margin
    if (settings['accordion-design-component-margin']) {
      result.margin = formatSpacingFormat(settings['accordion-design-component-margin'])
    }

    // Design - Colors
    if (settings['accordion-design-text-color']) {
      result.textColor = formatColorFormat({
        colorType: settings['accordion-design-text-color'] as any,
        colorSolid: settings['accordion-design-text-color-solid'],
        colorStartGradient: settings['accordion-design-text-color-start-gradient'],
        colorEndGradient: settings['accordion-design-text-color-end-gradient'],
        colorGradientDirection: settings['accordion-design-text-color-gradient-direction'],
      })
    }

    if (settings['accordion-design-content-color']) {
      result.contentColor = formatColorFormat({
        colorType: settings['accordion-design-content-color'] as any,
        colorSolid: settings['accordion-design-content-color-solid'],
        colorStartGradient: settings['accordion-design-content-color-start-gradient'],
        colorEndGradient: settings['accordion-design-content-color-end-gradient'],
        colorGradientDirection: settings['accordion-design-content-color-gradient-direction'],
      })
    }

    if (settings['accordion-design-title-color']) {
      result.titleColor = formatColorFormat({
        colorType: settings['accordion-design-title-color'] as any,
        colorSolid: settings['accordion-design-title-color-solid'],
        colorStartGradient: settings['accordion-design-title-color-start-gradient'],
        colorEndGradient: settings['accordion-design-title-color-end-gradient'],
        colorGradientDirection: settings['accordion-design-title-color-gradient-direction'],
      })
    }

    if (settings['accordion-design-group-header-color']) {
      result.groupHeaderColor = formatColorFormat({
        colorType: settings['accordion-design-group-header-color'] as any,
        colorSolid: settings['accordion-design-group-header-color-solid'],
        colorStartGradient: settings['accordion-design-group-header-color-start-gradient'],
        colorEndGradient: settings['accordion-design-group-header-color-end-gradient'],
        colorGradientDirection: settings['accordion-design-group-header-color-gradient-direction'],
      })
    }

    // Design - Typography
    if (settings['accordion-design-typography']) {
      result.typography = {}
      if (settings['accordion-design-typography']['accordion-design-title-typography']) {
        result.typography.title = formatTypographyFormat(settings['accordion-design-typography']['accordion-design-title-typography'])
      }
      if (settings['accordion-design-typography']['accordion-design-header-typography']) {
        result.typography.header = formatTypographyFormat(settings['accordion-design-typography']['accordion-design-header-typography'])
      }
      if (settings['accordion-design-typography']['accordion-design-content-typography']) {
        result.typography.content = formatTypographyFormat(settings['accordion-design-typography']['accordion-design-content-typography'])
      }
      if (settings['accordion-design-typography']['accordion-design-group-header-typography']) {
        result.typography.groupHeader = formatTypographyFormat(settings['accordion-design-typography']['accordion-design-group-header-typography'])
      }
    }

    // Behavior - Search
    if (settings['accordion-behavior-user-search'] !== undefined) {
      result.showSearch = settings['accordion-behavior-user-search']
    }

    // Behavior - Add Button
    if (settings['accordion-behavior-add-button-show'] !== undefined) {
      result.showAddButton = settings['accordion-behavior-add-button-show']
    }

    return result
  }

  /**
   * parse AI accordion input to accordion component settings
   */
  parse(input: AiAccordionInput): IAccordionComponentSettings {
    const result = deepMerge(ACCORDION_DEFAULT_TEMPLATE, {})

    // Data - Source (required)
    if (input.dataSource) {
      result['accordion-data-source'] = input.dataSource
    }

    // Data - Columns
    if (input.titleColumn) {
      result['accordion-data-title'] = input.titleColumn
    }

    if (input.contentColumn) {
      result['accordion-data-content'] = input.contentColumn
    }

    if (input.icon) {
      result['accordion-data-icon'] = parseToVariableSelect(input.icon)
    }

    if (input.expandedIcon) {
      result['accordion-data-expanded-icon'] = input.expandedIcon
    }

    // Data - Component Title
    if (input.accordionTitle) {
      result['accordion-data-comp-title'] = parseToVariableText(input.accordionTitle)
    }

    // Data - Sort
    if (input.sortBy) {
      // Note: sort-by field might not be in template, but we'll handle it if needed
    }

    if (input.sortType) {
      result['accordion-data-sort-type'] = input.sortType
    }

    // Design - Width
    if (input.width) {
      result['accordion-design-width'] = parseWidthFormat(input.width)
    }

    // Design - Alignment
    if (input.alignment) {
      const alignmentParsed = parseAlignmentFormat(input.alignment)
      if (alignmentParsed.hAlign) {
        result['accordion-design-alignment-component'] = alignmentParsed.hAlign
      }
    }

    if (input.titleAlignment) {
      const titleAlignParsed = parseAlignmentFormat(input.titleAlignment)
      if (titleAlignParsed.hAlign) {
        result['accordion-design-alignment-title'] = titleAlignParsed.hAlign
      }
    }

    // Design - Margin
    if (input.margin) {
      result['accordion-design-component-margin'] = parseSpacingFormat(input.margin)
    }

    // Design - Colors
    if (input.textColor) {
      const textColorParsed = parseColorFormat(input.textColor)
      result['accordion-design-text-color'] = textColorParsed.colorType
      result['accordion-design-text-color-solid'] = textColorParsed.colorSolid
      result['accordion-design-text-color-start-gradient'] = textColorParsed.colorStartGradient
      result['accordion-design-text-color-end-gradient'] = textColorParsed.colorEndGradient
      result['accordion-design-text-color-gradient-direction'] = textColorParsed.colorGradientDirection
    }

    if (input.contentColor) {
      const contentColorParsed = parseColorFormat(input.contentColor)
      result['accordion-design-content-color'] = contentColorParsed.colorType
      result['accordion-design-content-color-solid'] = contentColorParsed.colorSolid
      result['accordion-design-content-color-start-gradient'] = contentColorParsed.colorStartGradient
      result['accordion-design-content-color-end-gradient'] = contentColorParsed.colorEndGradient
      result['accordion-design-content-color-gradient-direction'] = contentColorParsed.colorGradientDirection
    }

    if (input.titleColor) {
      const titleColorParsed = parseColorFormat(input.titleColor)
      result['accordion-design-title-color'] = titleColorParsed.colorType
      result['accordion-design-title-color-solid'] = titleColorParsed.colorSolid
      result['accordion-design-title-color-start-gradient'] = titleColorParsed.colorStartGradient
      result['accordion-design-title-color-end-gradient'] = titleColorParsed.colorEndGradient
      result['accordion-design-title-color-gradient-direction'] = titleColorParsed.colorGradientDirection
    }

    if (input.groupHeaderColor) {
      const groupHeaderColorParsed = parseColorFormat(input.groupHeaderColor)
      result['accordion-design-group-header-color'] = groupHeaderColorParsed.colorType
      result['accordion-design-group-header-color-solid'] = groupHeaderColorParsed.colorSolid
      result['accordion-design-group-header-color-start-gradient'] = groupHeaderColorParsed.colorStartGradient
      result['accordion-design-group-header-color-end-gradient'] = groupHeaderColorParsed.colorEndGradient
      result['accordion-design-group-header-color-gradient-direction'] = groupHeaderColorParsed.colorGradientDirection
    }

    // Design - Typography
    if (input.typography) {
      if (input.typography.title) {
        const titleTypo = parseTypographyFormat(input.typography.title)
        result['accordion-design-typography']['accordion-design-title-typography'] = titleTypo
      }

      if (input.typography.header) {
        const headerTypo = parseTypographyFormat(input.typography.header)
        result['accordion-design-typography']['accordion-design-header-typography'] = headerTypo
      }

      if (input.typography.content) {
        const contentTypo = parseTypographyFormat(input.typography.content)
        result['accordion-design-typography']['accordion-design-content-typography'] = contentTypo
      }

      if (input.typography.groupHeader) {
        const groupHeaderTypo = parseTypographyFormat(input.typography.groupHeader)
        result['accordion-design-typography']['accordion-design-group-header-typography'] = groupHeaderTypo
      }
    }

    // Behavior
    if (input.showSearch !== undefined) {
      result['accordion-behavior-user-search'] = input.showSearch
    }

    if (input.showAddButton !== undefined) {
      result['accordion-behavior-add-button-show'] = input.showAddButton
    }

    if (input.multiExpand !== undefined) {
      // Note: multi-expand field might not be in template, but we'll handle it if needed
    }

    return result
  }
}

